import { useState } from "react";
import {
  useApproveSowingInstruction,
  useRecordSowing,
  useRecordGermination,
  useRevokeSowingInstruction,
} from "../../hooks/useSowingInstructions";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { SowingConfirmBody } from "./SowingConfirmSummary";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import { useGreenhouses } from "@/features/farms/hooks/useGreenhouses";
import { useBaysByGreenhouseId } from "@/features/farms/hooks/useGreenhouseBays";
import { useBedsByBayId } from "@/features/farms/hooks/useGreenhouseBeds";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import { getErrorMessage } from "@/shared/utils/errorHandler";

interface SowingInstructionDialogProps {
  instruction: SowingInstruction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/* Approve Dialog */
export const ApproveSowingInstructionDialog = ({
  instruction,
  open,
  onOpenChange,
}: SowingInstructionDialogProps) => {
  const [notes, setNotes] = useState("");
  const approveMutation = useApproveSowingInstruction();
  const notify = useNotification();
  const confirm = useConfirm();
  const { data: units = [] } = useUnitsOfMeasure();

  const handleSubmit = () => {
    confirm({
      title: "Approve sowing instruction?",
      description: "This instruction will be approved for sowing.",
      confirmLabel: "Approve",
      body: SowingConfirmBody(instruction, [
        { label: "Quantity to sow", value: `${instruction.quantityToSow}` },
      ]),
      onConfirm: () =>
        approveMutation
          .mutateAsync({
            sowingInstructionId: instruction.id,
            approved: true,
            notes,
          })
          .then(() => {
            setNotes("");
            onOpenChange(false);
            notify.success("Sowing instruction approved!");
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve Sowing Instruction</DialogTitle>
          <DialogDescription>
            {instruction.variety} - {instruction.quantityToSow}{" "}
            {units.find((u) => u.value === instruction.unit)?.name ?? "units"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Approval notes (optional)"
              disabled={approveMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={approveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? "Approving..." : "Approve"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* Record Sowing Dialog */
export const RecordSowingDialog = ({
  instruction,
  open,
  onOpenChange,
}: SowingInstructionDialogProps) => {
  const today = new Date().toISOString().split("T")[0];
  const alreadySown = instruction.quantitySown ?? 0;
  const remaining = Math.max(0, instruction.quantityToSow - alreadySown);
  const [formData, setFormData] = useState({
    actualSowingDate: instruction.scheduledSowingDate?.split("T")[0] || today,
    quantitySown: remaining.toString(),
    quantitySownUnitOfMeasure: instruction.unit?.toString() ?? "",
    seedsUsedQuantity: "",
    seedsUsedUnitOfMeasure: "2", // Grams — typical unit for seed weight
    seedsReturnedQuantity: "",
    seedsReturnedUnitOfMeasure: "2", // Grams
    notes: "",
  });

  const { data: units = [] } = useUnitsOfMeasure();
  const recordSowingMutation = useRecordSowing();
  const notify = useNotification();
  const confirm = useConfirm();

  const handleSubmit = () => {
    if (!formData.actualSowingDate) {
      notify.error("Please provide the actual sowing date");
      return;
    }

    // if (formData.actualSowingDate > today) {
    //   notify.error("Actual sowing date cannot be in the future");
    //   return;
    // }

    if (formData.seedsUsedQuantity && parseFloat(formData.seedsUsedQuantity) < 0) {
      notify.error("Seeds used quantity cannot be negative");
      return;
    }

    if (formData.seedsReturnedQuantity && parseFloat(formData.seedsReturnedQuantity) < 0) {
      notify.error("Seeds returned quantity cannot be negative");
      return;
    }

    const requestedQty = parseFloat(formData.quantitySown);
    if (!Number.isFinite(requestedQty) || requestedQty <= 0) {
      notify.error("Quantity sown must be greater than zero");
      return;
    }
    if (requestedQty > remaining) {
      notify.error(`Quantity exceeds remaining (${remaining})`);
      return;
    }

    const bedId = instruction.greenhouseBedId;

    confirm({
      title: "Record sowing?",
      description: "The sown quantity will be recorded for this instruction.",
      confirmLabel: "Record sowing",
      body: SowingConfirmBody(instruction, [
        { label: "Sowing date", value: formData.actualSowingDate },
        { label: "Quantity sown", value: formData.quantitySown },
      ]),
      onConfirm: () =>
        recordSowingMutation
          .mutateAsync({
            sowingInstructionId: instruction.id,
            actualSowingDate: formData.actualSowingDate,
            quantitySown: parseFloat(formData.quantitySown),
            quantitySownUnitOfMeasure: formData.quantitySownUnitOfMeasure
              ? parseInt(formData.quantitySownUnitOfMeasure)
              : undefined,
            seedsUsedQuantity: formData.seedsUsedQuantity
              ? parseFloat(formData.seedsUsedQuantity)
              : undefined,
            seedsUsedUnitOfMeasure: formData.seedsUsedQuantity
              ? parseInt(formData.seedsUsedUnitOfMeasure)
              : undefined,
            seedsReturnedQuantity: formData.seedsReturnedQuantity
              ? parseFloat(formData.seedsReturnedQuantity)
              : undefined,
            seedsReturnedUnitOfMeasure: formData.seedsReturnedQuantity
              ? parseInt(formData.seedsReturnedUnitOfMeasure)
              : undefined,
            bedIds: bedId ? [bedId] : undefined,
            notes: formData.notes || undefined,
          })
          .then(() => {
            onOpenChange(false);
            notify.success("Sowing recorded successfully!");
          })
          .catch((error: unknown) => {
            notify.error(getErrorMessage(error) || "Failed to record sowing");
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record Sowing</DialogTitle>
          <DialogDescription>{instruction.variety}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {alreadySown > 0 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100">
              <span className="font-medium">{alreadySown} of {instruction.quantityToSow} already sown</span>
              <> — recording {remaining} more</>
            </div>
          )}
          {instruction.seedSource === 2 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <span className="font-medium">Customer Seeds</span>
              {instruction.seedBatchNumber && (
                <> — Batch: {instruction.seedBatchNumber}</>
              )}
              {instruction.seedBatchAvailableQuantity != null && (
                <div className="mt-1 text-xs">
                  Available in batch:{" "}
                  <span className="font-semibold">
                    {instruction.seedBatchAvailableQuantity} {instruction.seedBatchUnit ?? ""}
                  </span>
                </div>
              )}
            </div>
          )}
          {instruction.seedSource === 1 && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
              <span className="font-medium">Company Seeds</span>
            </div>
          )}
          <Field>
            <FieldLabel>Actual Sowing Date <span className="text-destructive">*</span></FieldLabel>
            <DatePicker
              max={today}
              value={formData.actualSowingDate}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, actualSowingDate: value }))
              }
              disabled={recordSowingMutation.isPending}
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field>
              <FieldLabel>Quantity Sown <span className="text-destructive">*</span></FieldLabel>
              <Input
                type="number"
                step="0.01"
                value={formData.quantitySown}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantitySown: e.target.value,
                  }))
                }
                disabled={recordSowingMutation.isPending}
              />
            </Field>
            <Field>
              <FieldLabel>Unit</FieldLabel>
              <Select
                value={formData.quantitySownUnitOfMeasure}
                onValueChange={(v) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantitySownUnitOfMeasure: v,
                  }))
                }
                disabled={recordSowingMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.value} value={u.value.toString()}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field>
              <FieldLabel>
                Seeds Used{" "}
                <span className="text-muted-foreground text-xs">
                  (optional)
                </span>
              </FieldLabel>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.seedsUsedQuantity}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    seedsUsedQuantity: e.target.value,
                  }))
                }
                disabled={recordSowingMutation.isPending}
                placeholder="0.00"
              />
            </Field>
            <Field>
              <FieldLabel>Unit</FieldLabel>
              <Select
                value={formData.seedsUsedUnitOfMeasure}
                onValueChange={(v) =>
                  setFormData((prev) => ({
                    ...prev,
                    seedsUsedUnitOfMeasure: v,
                  }))
                }
                disabled={recordSowingMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.value} value={u.value.toString()}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field>
              <FieldLabel>
                Seeds Returned{" "}
                <span className="text-muted-foreground text-xs">
                  (optional)
                </span>
              </FieldLabel>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.seedsReturnedQuantity}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    seedsReturnedQuantity: e.target.value,
                  }))
                }
                disabled={recordSowingMutation.isPending}
                placeholder="0.00"
              />
            </Field>
            <Field>
              <FieldLabel>Unit</FieldLabel>
              <Select
                value={formData.seedsReturnedUnitOfMeasure}
                onValueChange={(v) =>
                  setFormData((prev) => ({
                    ...prev,
                    seedsReturnedUnitOfMeasure: v,
                  }))
                }
                disabled={recordSowingMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.value} value={u.value.toString()}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Sowing notes"
              disabled={recordSowingMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={recordSowingMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={recordSowingMutation.isPending}
            >
              {recordSowingMutation.isPending
                ? "Recording..."
                : "Record Sowing"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* Record Germination Dialog */
export const RecordGerminationDialog = ({
  instruction,
  open,
  onOpenChange,
}: SowingInstructionDialogProps) => {
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    germinationStartedDate:
      instruction.expectedGerminationDate?.split("T")[0] || today,
    germinationCompletedDate: today,
    quantityGerminated: instruction.quantityToSow.toString(),
    quantityGerminatedUnitOfMeasure: instruction.unit?.toString() ?? "",
    greenhouseId: instruction.greenhouseId || "",
    bayId: "",
    greenhouseBedId: "",
    notes: "",
  });

  const { data: units = [] } = useUnitsOfMeasure();
  const greenhousesQuery = useGreenhouses();
  const baysQuery = useBaysByGreenhouseId(formData.greenhouseId);
  const bedsQuery = useBedsByBayId(formData.bayId);
  const recordGerminationMutation = useRecordGermination();
  const notify = useNotification();
  const confirm = useConfirm();

  const handleSubmit = () => {
    if (!formData.germinationStartedDate || !formData.germinationCompletedDate) {
      notify.error("Please provide both germination dates");
      return;
    }

    if (!formData.greenhouseId) {
      notify.error("Please select a greenhouse");
      return;
    }

    confirm({
      title: "Record germination?",
      description: "The germinated quantity will be recorded for this instruction.",
      confirmLabel: "Record germination",
      body: SowingConfirmBody(instruction, [
        { label: "Quantity germinated", value: formData.quantityGerminated },
      ]),
      onConfirm: () =>
        recordGerminationMutation
          .mutateAsync({
            sowingInstructionId: instruction.id,
            germinationStartedDate: formData.germinationStartedDate,
            germinationCompletedDate: formData.germinationCompletedDate,
            quantityGerminated: parseFloat(formData.quantityGerminated),
            quantityGerminatedUnitOfMeasure: formData.quantityGerminatedUnitOfMeasure
              ? parseInt(formData.quantityGerminatedUnitOfMeasure)
              : undefined,
            greenhouseId: formData.greenhouseId || undefined,
            greenhouseBedId: formData.greenhouseBedId || undefined,
            notes: formData.notes || undefined,
          })
          .then(() => {
            onOpenChange(false);
            notify.success("Germination recorded successfully!");
          })
          .catch((error: unknown) => {
            notify.error(
              getErrorMessage(error) || "Failed to record germination"
            );
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record Germination</DialogTitle>
          <DialogDescription>{instruction.variety}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>
              Germination Started Date{" "}
              <span className="text-destructive">*</span>
            </FieldLabel>
            <DatePicker
              value={formData.germinationStartedDate}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  germinationStartedDate: value,
                }))
              }
              disabled={recordGerminationMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>
              Germination Completed Date{" "}
              <span className="text-destructive">*</span>
            </FieldLabel>
            <DatePicker
              max={today}
              value={formData.germinationCompletedDate}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  germinationCompletedDate: value,
                }))
              }
              disabled={recordGerminationMutation.isPending}
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field>
              <FieldLabel>
                Quantity Germinated{" "}
                <span className="text-destructive">*</span>
              </FieldLabel>
              <Input
                type="number"
                step="0.01"
                value={formData.quantityGerminated}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantityGerminated: e.target.value,
                  }))
                }
                disabled={recordGerminationMutation.isPending}
              />
            </Field>
            <Field>
              <FieldLabel>Unit</FieldLabel>
              <Select
                value={formData.quantityGerminatedUnitOfMeasure}
                onValueChange={(v) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantityGerminatedUnitOfMeasure: v,
                  }))
                }
                disabled={recordGerminationMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.value} value={u.value.toString()}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="border-t pt-4">
            <p className="text-sm font-semibold mb-3">Location</p>
            <div className="space-y-3">
              <Field>
                <FieldLabel>Greenhouse <span className="text-destructive">*</span></FieldLabel>
                <Select
                  value={formData.greenhouseId}
                  onValueChange={(v) =>
                    setFormData((prev) => ({
                      ...prev,
                      greenhouseId: v,
                      bayId: "",
                      greenhouseBedId: "",
                    }))
                  }
                  disabled={recordGerminationMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select greenhouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {greenhousesQuery.data?.map((gh) => (
                      <SelectItem key={gh.id} value={gh.id}>
                        {gh.greenhouseName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {formData.greenhouseId && (
                <Field>
                  <FieldLabel>Bay <span className="text-muted-foreground text-xs">(optional)</span></FieldLabel>
                  <Select
                    value={formData.bayId}
                    onValueChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        bayId: v,
                        greenhouseBedId: "",
                      }))
                    }
                    disabled={recordGerminationMutation.isPending}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bay (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {baysQuery.data?.map((bay) => (
                        <SelectItem key={bay.id} value={bay.id}>
                          {bay.bayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {formData.bayId && (
                <Field>
                  <FieldLabel>Bed <span className="text-muted-foreground text-xs">(optional)</span></FieldLabel>
                  <Select
                    value={formData.greenhouseBedId}
                    onValueChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        greenhouseBedId: v,
                      }))
                    }
                    disabled={recordGerminationMutation.isPending}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bed (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {bedsQuery.data?.map((bed) => (
                        <SelectItem key={bed.id} value={bed.id}>
                          {bed.bedNumber}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
          </div>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Germination notes"
              disabled={recordGerminationMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={recordGerminationMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={recordGerminationMutation.isPending}
            >
              {recordGerminationMutation.isPending
                ? "Recording..."
                : "Record Germination"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* Revoke Dialog */
export const RevokeSowingInstructionDialog = ({
  instruction,
  open,
  onOpenChange,
}: SowingInstructionDialogProps) => {
  const [reason, setReason] = useState("");
  const revokeMutation = useRevokeSowingInstruction();
  const notify = useNotification();

  const handleSubmit = () => {
    if (!reason) {
      notify.error("Please provide a reason for revocation");
      return;
    }

    revokeMutation.mutate(
      {
        sowingInstructionId: instruction.id,
        reason,
      },
      {
        onSuccess: () => {
          setReason("");
          onOpenChange(false);
          notify.success("Sowing instruction revoked!");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Revoke Sowing Instruction</DialogTitle>
          <DialogDescription>{instruction.variety}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>Reason for Revocation *</FieldLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this instruction being revoked?"
              disabled={revokeMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={revokeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
